import { generateSeoFriendlyKeywords } from 'utils/targeted-url-helper';
import { getTargetedUrlPropertyTypeSelection, HOMES_TARGETED_URL_PROPERTY_TYPE } from 'utils/listing-query-helper';
import { LISTING_STATUS_SOLD, LISTING_STATUS_PAST_LISTINGS, LISTING_STATUS_LEASED, LISTING_STATUS_EXPIRED_LISTINGS, FILTER_WITH_GARAGE, FILTER_ON_WATERFRONT, FILTER_WITH_SWIMMING_POOL, HOME_TYPE_HOUSES, HOME_TYPE_TOWNHOUSES, HOME_TYPE_CONDOS, HOME_TYPE_LAND, HOME_TYPE_COMMERCIAL, HOME_TYPE_FARMS } from 'data/addresses/go-search/addressPathParser';
import { getLocalePrefix } from 'utils/locale/get-localized-url';
import { getTenantById } from '@tenants/manager';
import { getTenantSeoConfig } from '@tenants/utils/configs';
import { stripAffiliateHandle } from 'utils/urls/strip-affiliate-handle';

import type { ListingParams } from 'contexts/preferences/listing-params/types';
import type { TenantId } from '@tenants/types';
/**
 * Helper function to determine if a path is an SEO-friendly path.
 * @param pathPart - The path part to check
 * @returns True if the path is an SEO-friendly path, false otherwise
 */
function checkIfSeoPath(pathPart: string) {
  const seoFriendlyKeywordsList = [LISTING_STATUS_SOLD, LISTING_STATUS_PAST_LISTINGS, LISTING_STATUS_LEASED, LISTING_STATUS_EXPIRED_LISTINGS, FILTER_WITH_GARAGE, FILTER_ON_WATERFRONT, FILTER_WITH_SWIMMING_POOL, HOME_TYPE_HOUSES, HOME_TYPE_TOWNHOUSES, HOME_TYPE_CONDOS, HOME_TYPE_LAND, HOME_TYPE_COMMERCIAL, HOME_TYPE_FARMS];
  return seoFriendlyKeywordsList.includes(pathPart);
}

/**
 * Helper function to get the locale prefix for canonical URLs.
 * @param locale - The locale code
 * @param tenantId - The tenant ID
 * @returns The locale prefix string (e.g., '/fr-CA') or empty string
 */
function getCanonicalLocalePrefix(locale: string, tenantId: TenantId): string {
  if (!locale || !tenantId) {
    return '';
  }
  
  const tenant = getTenantById(tenantId);
  const seoConfig = getTenantSeoConfig(tenant);
  
  if (seoConfig?.useLocaleAwareCanonicals) {
    return getLocalePrefix({ locale, tenantId });
  }
  
  return '';
}

export interface GenerateCanonicalUrlParams {
  /** The full path from Next.js router (including query parameters) */
  asPath: string;
  /** The base host of the application (e.g., https://www.zoocasa.com) */
  baseHost: string;
  /** The locale code (e.g., 'en-CA', 'fr-CA') */
  locale: string;
  /** The tenant ID */
  tenantId: TenantId;
  /** Optional listing filters for SEO-friendly keywords */
  filters?: ListingParams['filter'];
}

/**
 * Generates a canonical URL by removing all query parameters.
 * For tenants with `useLocaleAwareCanonicals` enabled, includes locale prefix for non-default locales (e.g., /fr-CA/).
 * 
 * @param params - The parameters for generating the canonical URL
 * @returns The canonical URL without query parameters
 */
export function generateCanonicalUrl(params: GenerateCanonicalUrlParams): string {
  const { asPath: rawAsPath, baseHost, filters, locale, tenantId } = params;
  const asPath = stripAffiliateHandle(rawAsPath);

  // Get the locale prefix for the canonical URL
  const localePrefix = getCanonicalLocalePrefix(locale, tenantId);

  // Ensure the baseHost ends with a slash for consistency
  const formattedHost = baseHost.endsWith('/') ? baseHost.slice(0, -1) : baseHost;

  // Extract the base path (ignore anything after `?`)
  let [basePath] = asPath.replace('/filter', '').split('?');
  // For neighbourhood pages, we just want to return the base path. For paths with more than 1 part, they are of the form
  // /[city]-real-estate/[neighbourhood] or /[city]-real-estate/[SEO friendly keyword]. If it's the former, return the base path
  const pathParts = basePath.split('/').slice(1);
  const pathPart = pathParts[1] || '';
  const isNeighbourhoodPage = !checkIfSeoPath(basePath) && pathPart.length > 0;

  if (isNeighbourhoodPage) {
    return `${formattedHost}${localePrefix}${basePath}`;
  }

  let seoFriendlyKeywords = '';
  if (filters) {
    // Check if more than one targeted url filter is on
    const activePropertyType = getTargetedUrlPropertyTypeSelection(filters.homeType);
    const isSpecificHomeType = activePropertyType !== HOMES_TARGETED_URL_PROPERTY_TYPE;
    const isForRent = filters.rental;
    const isLeasedStatus = isForRent && filters.status === 'not-available-sold';
    const isSpecificStatus = filters.status !== 'available';
    const isGarageOn = filters.garage;
    const isOnWaterfrontOn = filters.waterfront;
    const isWithSwimmingPoolOn = filters.pool;
    const targetedVariantsOnAmount = [isSpecificHomeType, isForRent, isSpecificStatus, isGarageOn, isOnWaterfrontOn, isWithSwimmingPoolOn].filter(Boolean).length;
    const isMultipleTargetedUrlFilters = targetedVariantsOnAmount > 1;
    const isLeased = targetedVariantsOnAmount == 2 && isLeasedStatus;

    // Remove any targeted urls as this will be appended below
    basePath = basePath.replace(/(-real-estate).*/, '$1');

    if (!isMultipleTargetedUrlFilters || isLeased) {
      seoFriendlyKeywords = generateSeoFriendlyKeywords({
        isSinglePropertyType: isSpecificHomeType,
        activePropertyType,
        isLeasedStatus,
        isRentalTheSame: !isForRent,
        isListingStatusTheSame: !isSpecificStatus,
        isOpenHouseTheSame: !filters.openHouse,
        isWaterfrontTheSame: !isOnWaterfrontOn,
        isGarageTheSame: !isGarageOn,
        isPoolTheSame: !isWithSwimmingPoolOn,
        filters: filters,
      });
    }
  }

  return `${formattedHost}${localePrefix}${basePath}${seoFriendlyKeywords}`;
}
